import {
  Auth0Client,
  type User,
} from "@auth0/auth0-spa-js";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { RuntimeConfig } from "../lib/config";
import { callbackUrl } from "../lib/config";

interface AuthState {
  ready: boolean;
  available: boolean;
  authenticated: boolean;
  user?: User;
  error?: string;
  login: () => Promise<void>;
  signup: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  invalidateSession: (message?: string | null) => void;
}

type AuthFlow = {
  connection?: "google-oauth2";
  screenHint?: "signup";
};

export function buildAuthRedirect(redirectUri: string, flow: AuthFlow = {}) {
  return {
    appState: { returnTo: redirectUri },
    authorizationParams: {
      redirect_uri: redirectUri,
      ...(flow.connection ? { connection: flow.connection } : {}),
      ...(flow.screenHint ? { screen_hint: flow.screenHint } : {}),
    },
  };
}

const AuthContext = createContext<AuthState | null>(null);

type SessionClient = Pick<Auth0Client, "checkSession" | "handleRedirectCallback">;

const AUTH_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  email_verification_required:
    "Vérifie l’adresse e-mail de ce compte, y compris dans les indésirables, puis reconnecte-toi à VAELOR.",
};

export const SESSION_EXPIRED_MESSAGE = "Ta session a expiré. Reconnecte-toi.";

const AUTH_SESSION_EXPIRY_CODES = new Set([
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "login_required",
  "missing_refresh_token",
  "session_expired",
]);

export function isAuthSessionExpiryError(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const source = value as { code?: unknown; error?: unknown };
  const code = typeof source.error === "string"
    ? source.error
    : typeof source.code === "string"
      ? source.code
      : undefined;
  return Boolean(code && AUTH_SESSION_EXPIRY_CODES.has(code));
}

export function authErrorMessage(description?: string | null): string {
  const normalized = description?.trim();
  if (!normalized) return "Connexion refusée par le fournisseur d’identité.";
  return AUTH_ERROR_MESSAGES[normalized] ?? normalized;
}

export function clearAuthReturnUrl(
  search: string,
  replace: () => void,
): boolean {
  const query = new URLSearchParams(search);
  const isAuthReturn = query.has("error") || (query.has("code") && query.has("state"));
  if (!isAuthReturn) return false;
  replace();
  return true;
}

export async function initializeAuthSession(
  auth0: SessionClient,
  search: string,
): Promise<boolean> {
  const query = new URLSearchParams(search);
  if (query.has("error")) {
    throw new Error(authErrorMessage(query.get("error_description")));
  }
  if (query.has("code") && query.has("state")) {
    await auth0.handleRedirectCallback();
    return true;
  }
  await auth0.checkSession();
  return false;
}

async function withDeadline<T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function AuthProvider({
  config,
  children,
}: PropsWithChildren<{ config: RuntimeConfig }>) {
  const [client, setClient] = useState<Auth0Client>();
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User>();
  const [error, setError] = useState<string>();
  const redirectUri = useMemo(
    () => callbackUrl(window.location, import.meta.env.BASE_URL),
    [],
  );

  useEffect(() => {
    let active = true;
    if (!config.identityConfigured) {
      setClient(undefined);
      setAuthenticated(false);
      setUser(undefined);
      setError(undefined);
      setReady(true);
      return () => {
        active = false;
      };
    }

    setReady(false);
    setClient(undefined);
    setAuthenticated(false);
    setUser(undefined);
    void (async () => {
      try {
        const auth0 = new Auth0Client({
          domain: config.auth0Domain,
          clientId: config.auth0ClientId,
          authorizationParams: {
            audience: config.auth0Audience,
            redirect_uri: redirectUri,
            scope: config.auth0Scope,
          },
          cacheLocation: "memory",
          httpTimeoutInSeconds: 15,
          useRefreshTokens: false,
        });
        if (!active) return;
        setClient(auth0);
        setAuthenticated(false);
        setUser(undefined);

        const handledCallback = await withDeadline(
          initializeAuthSession(auth0, window.location.search),
          20_000,
          "Le service d’identité met trop de temps à répondre. Réessaie dans un instant.",
        );
        if (!active) return;
        if (handledCallback) {
          clearAuthReturnUrl(window.location.search, () => {
            window.history.replaceState({}, document.title, redirectUri);
          });
        }

        const isAuthenticated = await auth0.isAuthenticated();
        if (!active) return;
        const user = isAuthenticated ? await auth0.getUser() : undefined;
        if (!active) return;
        setAuthenticated(isAuthenticated);
        setUser(user);
      } catch (cause) {
        if (active) {
          clearAuthReturnUrl(window.location.search, () => {
            window.history.replaceState({}, document.title, redirectUri);
          });
          setError(cause instanceof Error ? cause.message : "L’identité n’a pas pu être initialisée.");
        }
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [config, redirectUri]);

  const beginAuth = useCallback(async (flow: AuthFlow = {}) => {
    if (!config.identityConfigured) {
      const cause = new Error(
        "L’identité VAELOR attend encore son domaine Auth0 de production. Aucun compte ne peut être créé avec la configuration de démonstration.",
      );
      setError(cause.message);
      throw cause;
    }
    if (!client) {
      const cause = new Error(error ?? "Le service d’identité n’est pas prêt.");
      if (!error) setError(cause.message);
      throw cause;
    }
    setError(undefined);
    try {
      await client.loginWithRedirect(buildAuthRedirect(redirectUri, flow));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "La connexion n’a pas pu être ouverte.");
      throw cause;
    }
  }, [client, config.identityConfigured, error, redirectUri]);

  const login = useCallback(() => beginAuth(), [beginAuth]);
  const signup = useCallback(() => beginAuth({ screenHint: "signup" }), [beginAuth]);
  const loginWithGoogle = useCallback(
    () => beginAuth({ connection: "google-oauth2" }),
    [beginAuth],
  );

  const logout = useCallback(async () => {
    if (!client) return;
    await client.logout({
      logoutParams: { returnTo: redirectUri },
    });
  }, [client, redirectUri]);

  const invalidateSession = useCallback((message: string | null = SESSION_EXPIRED_MESSAGE) => {
    setAuthenticated(false);
    setUser(undefined);
    setError(message ?? undefined);
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!client || !authenticated) throw new Error("Une connexion est requise.");
    try {
      return await client.getTokenSilently({
        authorizationParams: {
          audience: config.auth0Audience,
          scope: config.auth0Scope,
        },
      });
    } catch (cause) {
      if (isAuthSessionExpiryError(cause)) invalidateSession();
      throw cause;
    }
  }, [authenticated, client, config, invalidateSession]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      available: config.identityConfigured,
      authenticated,
      user,
      error,
      login,
      signup,
      loginWithGoogle,
      logout,
      getAccessToken,
      invalidateSession,
    }),
    [authenticated, config.identityConfigured, error, getAccessToken, invalidateSession, login, loginWithGoogle, logout, ready, signup, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth doit être utilisé dans AuthProvider.");
  return context;
}
